import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ordersAdvancedSearch } from '../../../api/orders';
import OrderSearchResult from './OrderSearchResult';
import { Search } from './Search'

export default function OrderSearch() {

  const [searchParams, setSearchParams] = useSearchParams();
  const [base] = useState(() => searchParams.get('base'), [searchParams]);
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState({ searchValue: '', selectedSearchTypes: [], page: 1, pageSize: 20 });
  const [searchResults, setSearchResults] = useState({});
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
  });
  const [initialFilters, setInitialFilters] = useState({...filters});
  const [initialSearchData, setInitialSearchData] = useState({ ...searchData });
  const searchTypes = [
    {value: 'orderNumber', label: 'Order Number'},
    {value: 'department', label: 'Department'},  
    {value: 'vendor', label: 'Vendors'},
    {value: 'submittedBy', label: 'Submitted By'},
    {value: 'manager', label: 'Manager'},
    {value: 'facilityName', label: 'Facility'},
    {value: 'vendorItemId', label: 'SKU'},
    {value: 'description', label: 'Description'}
  ];
  const filtersChanged = useMemo(() => JSON.stringify(filters) !== JSON.stringify(initialFilters), [initialFilters, filters]);
  const isSearchDirty = useMemo(() => searchData.searchValue !== initialSearchData.searchValue ||
    JSON.stringify(searchData.selectedSearchTypes) !== JSON.stringify(initialSearchData.selectedSearchTypes) ||
    filtersChanged, [searchData, initialSearchData, filtersChanged]);
  const getPrevOrderStartDate = () => new Date(new Date().setDate(new Date().getDate() - 29)).toISOString();
  const getPrevOrderEndDate = () => new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString();
  const startDate = useMemo(() => filters.startDate || getPrevOrderStartDate(), [filters]);
  const endDate = useMemo(() => filters.endDate || getPrevOrderEndDate(), [filters]);
  
  const searchValueTrimmed = searchData.searchValue?.trim();

  const getSearchTypeFromUrl = (type) => {
    if (searchParams.get(type)?.toLowerCase() === "true"){
      return searchTypes.find(searchType => searchType.value === type);
    }
    return null;
  };

  useEffect(() => {
    const urlSearchTypes = searchTypes.map(type => getSearchTypeFromUrl(type.value)).filter(t => t);
    const urlData = {
      selectedSearchTypes: urlSearchTypes,
      searchValue: searchParams.get('searchValue'),
      pageSize: searchData.pageSize
    };
    const page = parseInt(searchParams.get('page'), 10) || 1;
    setSearchData({...urlData, page});
    setInitialSearchData(urlData);
    handleSearch(urlData, page);
  }, []);

  const handleSearch = async (data, page) => {
    setSearchData({...data, page});
    if (!data.searchValue || !data.selectedSearchTypes?.length) return;
    const submitData = { searchValue: data.searchValue.trim(), page }
    data.selectedSearchTypes.forEach((type) => { submitData[type.value] = true; });
    setSearchParams({ ...submitData, base});
    setInitialSearchData({ ...data });
    setInitialFilters({...filters});
    setLoading(true);
    const searchResponse =  await ordersAdvancedSearch({
        startDate,
        endDate,
        pageSize: searchData.pageSize,
        ...submitData
      });
    setSearchResults({ searchResults: searchResponse.data.results, totalCount: searchResponse.data.totalCount });
    setLoading(false);
  };

  const searchDataMap = useMemo(() => searchResults?.searchResults?.map((searchResult) => 
    <OrderSearchResult
      searchResult={searchResult}
      search={searchValueTrimmed}
      key={searchResult.orderId}
    />
  ), [searchResults]);

  const handlePaginationChange = (page) => {
    setSearchData(data => ({...data, page}));
    handleSearch(searchData, page);
  }

  const basePath = '/dashboard/orders';
  const backToPath = useMemo(
    () => base.toLowerCase().includes("vendor") ? `${basePath}?filter=previous&start=${getPrevOrderStartDate()}&end=${getPrevOrderEndDate()}&viewByVendor=1` :
          base.toLowerCase().includes("previous") ? `${basePath}?filter=previous&start=${getPrevOrderStartDate()}&end=${getPrevOrderEndDate()}` :
          base.toLowerCase().includes("pending") ? `${basePath}?filter=pending` : '', [base]
  );

  return <Search
    backToPath={backToPath}
    searchTypes={searchTypes}
    searchData={searchData}
    setSearchData={setSearchData}
    handleSearch={handleSearch}
    isSearchDisabled={!searchData.searchValue || !isSearchDirty || !searchData.selectedSearchTypes.length}
    loading={loading}
    handlePaginationChange={handlePaginationChange}
    searchDataMap={searchDataMap}
    searchResults={searchResults}
    filters={filters}
    setFilters={setFilters}
  />
}